import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Edit3, Table, FlaskConical } from "lucide-react";

// Mirrors PrescriptionTable.js's architecture (parse a pipe-delimited
// multiline string into editable rows, serialize back on every change) -
// same gp_* free-text field convention, just a simpler row shape since a
// test doesn't need dose/timing/duration.
export default function TestsTable({ value, onChange, disabled = false }) {
  const [mode, setMode] = useState("table"); // "table" | "raw"
  const [tests, setTests] = useState([]);

  const testInputRefs = useRef([]);
  const shouldFocusNewRowRef = useRef(false);
  const addBtnRef = useRef(null);
  const lastEmittedRef = useRef(null);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;

    if (!value || typeof value !== "string" || value.trim() === "") {
      setTests([{ name: "", notes: "" }]);
      return;
    }

    const lines = value.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map((line) => {
      const cleanLine = line.replace(/^\d+[\.\)]\s*|^-\s*/, "");
      const parts = cleanLine.split("|").map((p) => p.trim());
      return { name: parts[0] || "", notes: parts[1] || "" };
    });
    setTests(parsed.length > 0 ? parsed : [{ name: "", notes: "" }]);
  }, [value]);

  useEffect(() => {
    if (shouldFocusNewRowRef.current) {
      shouldFocusNewRowRef.current = false;
      const lastIndex = tests.length - 1;
      if (lastIndex >= 0 && testInputRefs.current[lastIndex]) {
        testInputRefs.current[lastIndex].focus();
      }
    }
  }, [tests.length]);

  const syncToParent = (updatedTests) => {
    setTests(updatedTests);
    const formatted = updatedTests
      .filter((t) => t.name.trim() !== "")
      .map((t, idx) => {
        const parts = [`${idx + 1}. ${t.name.trim()}`];
        if (t.notes && t.notes.trim()) parts.push(t.notes.trim());
        return parts.join(" | ");
      })
      .join("\n");
    lastEmittedRef.current = formatted;
    onChange({ target: { name: "gp_tests", value: formatted } });
  };

  const handleRowChange = (index, field, val) => {
    const next = [...tests];
    next[index] = { ...next[index], [field]: val };
    syncToParent(next);
  };

  const handleAddRow = (prefillName = "") => {
    shouldFocusNewRowRef.current = true;
    if (tests.length === 1 && tests[0].name.trim() === "" && prefillName) {
      syncToParent([{ ...tests[0], name: prefillName }]);
      return;
    }
    syncToParent([...tests, { name: prefillName, notes: "" }]);
  };

  const handleRemoveRow = (index) => {
    if (tests.length <= 1) {
      syncToParent([{ name: "", notes: "" }]);
      return;
    }
    syncToParent(tests.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-3.5 shadow-2xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-2.5 mb-2.5">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-50 border border-purple-200 text-purple-600 shrink-0 select-none">
            <FlaskConical className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 leading-none">
              Tests / Investigations
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
              Diagnostic tests advised for this patient, if any.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
          <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setMode("table")}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors flex items-center gap-1 cursor-pointer ${mode === "table" ? "bg-white text-gray-900 shadow-2xs" : "text-gray-500 hover:text-gray-800"
                }`}
            >
              <Table className="w-3 h-3 text-purple-600" />
              Table
            </button>
            <button
              type="button"
              onClick={() => setMode("raw")}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors flex items-center gap-1 cursor-pointer ${mode === "raw" ? "bg-white text-gray-900 shadow-2xs" : "text-gray-500 hover:text-gray-800"
                }`}
            >
              <Edit3 className="w-3 h-3 text-gray-600" />
              Free Text
            </button>
          </div>
        </div>
      </div>

      {mode === "table" ? (
        <div className="space-y-2.5">
          <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            <table className="w-full text-left border-collapse min-w-[420px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/90 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                  <th className="py-1.5 px-2 w-8 text-center">#</th>
                  <th className="py-1.5 px-2 min-w-[200px]">Test Name</th>
                  <th className="py-1.5 px-2 min-w-[200px]">Notes (optional)</th>
                  <th className="py-1.5 px-1.5 w-8 text-center">Del</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tests.map((t, idx) => (
                  <tr key={idx} className="hover:bg-purple-50/20 transition-colors group">
                    <td className="py-1 px-2 text-center align-middle">
                      <span className="text-xs font-semibold text-gray-400 select-none">
                        {idx + 1}
                      </span>
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        ref={(el) => (testInputRefs.current[idx] = el)}
                        type="text"
                        value={t.name}
                        onChange={(e) => handleRowChange(idx, "name", e.target.value)}
                        placeholder="e.g. Complete Blood Count (CBC)"
                        className="w-full h-8 text-xs font-medium text-gray-900 border border-gray-300 rounded-md px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white placeholder:text-gray-400 transition-colors"
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="text"
                        value={t.notes || ""}
                        onChange={(e) => handleRowChange(idx, "notes", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Tab" && !e.shiftKey) {
                            e.preventDefault();
                            addBtnRef.current?.focus();
                          }
                        }}
                        placeholder="e.g. Fasting required"
                        className="w-full h-8 text-xs text-gray-800 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white placeholder:text-gray-400 transition-colors"
                      />
                    </td>
                    <td className="py-1 px-1.5 text-center align-middle">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                        title="Remove test"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pt-0.5">
            <button
              ref={addBtnRef}
              type="button"
              onClick={() => handleAddRow()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg font-semibold text-xs transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-purple-100"
            >
              <Plus className="w-3.5 h-3.5 text-purple-600" />
              Add Test
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-gray-600">
            Free-Text Tests / Investigations:
          </label>
          <textarea
            name="gp_tests"
            value={value || ""}
            onChange={onChange}
            placeholder="Type tests/investigations freely here..."
            className="w-full min-h-[100px] p-2.5 text-xs font-mono text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 bg-white"
          />
          <p className="text-[11px] text-gray-400">
            Tip: You can switch back to Table View anytime. Each line is stored as: Test Name | Notes
          </p>
        </div>
      )}
    </div>
  );
}
